import { useRef, useState } from "react";
import config from "../config";
import axios from "axios";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCheckCircle, faExclamationTriangle, faSpinner } from "@fortawesome/free-solid-svg-icons";

interface ReviewProps {
    email: string;
}

const Review = ({ email }: ReviewProps) => {
    const [name, setName] = useState('');
    const [typedEmail, setTypedEmail] = useState(email ? email : '');
    const [review, setReview] = useState('');
    const [company, setCompany] = useState(''); // honeypot — see the field below
    const [loading, setLoading] = useState(false);
    const [userReviewSubmitted, setUserReviewSubmitted] = useState(false);
    const [userError, setUserError] = useState(false);
    const renderedAt = useRef(Date.now());

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setUserError(false);
        setUserReviewSubmitted(false);
        setLoading(true);

        try {
            await axios.post(`${config.backendUrl}users/feedback`, {
                email: typedEmail,
                message: review,
                name: name || undefined,
                company, // honeypot: real users never see or fill this field
                renderedAt: renderedAt.current,
            }, {
                headers: { 'Content-Type': 'application/json' }
            });

            setUserReviewSubmitted(true);
            setTimeout(() => {
                window.location.reload();
            }, 5000);
        } catch {
            setUserError(true);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="web-page">
            <div className="page-shell page-shell--narrow review-page">
                <header className="page-header review-header">
                    <h1 className="page-title">Help us improve SponsorDB</h1>
                    <p className="page-lead">
                        Share feedback, report a bug, or suggest a feature — we read every message.
                    </p>
                </header>

                <div className="panel review-card">
                    <form className="review-form" onSubmit={handleSubmit}>
                        {/* Honeypot — invisible to real users (off-screen, not display:none
                            or type=hidden, since some bots specifically skip those), but a
                            plain <input> a bot's DOM scan will happily fill in. Server
                            rejects silently if it's non-empty. */}
                        <div style={{ position: 'absolute', left: '-9999px', width: '1px', height: '1px', overflow: 'hidden' }} aria-hidden="true">
                            <label htmlFor="company-input">Company</label>
                            <input
                                id="company-input"
                                name="company"
                                type="text"
                                tabIndex={-1}
                                autoComplete="off"
                                value={company}
                                onChange={(e) => setCompany(e.target.value)}
                            />
                        </div>

                        <div className="form-field">
                            <label className="form-label" htmlFor="name-input">
                                Name <span className="form-label-optional">(optional)</span>
                            </label>
                            <input
                                id="name-input"
                                className="input"
                                type="text"
                                placeholder="Your name"
                                value={name}
                                onChange={(e) => setName(e.target.value)}
                            />
                        </div>

                        <div className="form-field">
                            <label className="form-label" htmlFor="email-input">Email</label>
                            <input
                                value={typedEmail}
                                required
                                id="email-input"
                                className="input"
                                type="email"
                                placeholder="you@example.com"
                                onChange={(e) => setTypedEmail(e.target.value)}
                            />
                        </div>

                        <div className="form-field">
                            <label className="form-label" htmlFor="review-input">Message</label>
                            <textarea
                                required
                                id="review-input"
                                className="input form-textarea"
                                rows={5}
                                placeholder="What's working well? What could be better?"
                                value={review}
                                onChange={(e) => setReview(e.target.value)}
                            />
                        </div>

                        <div className="form-actions">
                            <button
                                type="submit"
                                className="btn btn-primary btn-full"
                                disabled={loading}
                            >
                                {loading ? (
                                    <>
                                        <FontAwesomeIcon icon={faSpinner} spin />
                                        {' '}Sending…
                                    </>
                                ) : (
                                    'Submit feedback'
                                )}
                            </button>
                            {userReviewSubmitted && (
                                <div className="form-message form-message--success" role="status">
                                    <FontAwesomeIcon icon={faCheckCircle} />
                                    Thanks — we'll review your feedback soon.
                                </div>
                            )}
                            {userError && (
                                <div className="form-message form-message--error" role="alert">
                                    <FontAwesomeIcon icon={faExclamationTriangle} />
                                    Something went wrong. Please try again.
                                </div>
                            )}
                        </div>
                    </form>
                </div>
            </div>
        </div>
    );
};

export default Review;
